import { MeasureCanvas } from "./MeasureCanvas";

import { getFontSize, getFontWeight } from "../../../../utils/typographyUtils";

/*
  truncates text to fit within a given width
  makes a new measureCanvas if none is provided
  requires a fontTemplate string to be passed in in format "{weightPixels} {sizePixels}px {fontFamily}"
*/
export function truncateText(
	text: string,
	fontTemplate: string,
	maxWidth: number,
	truncationString = "...",
	measureCanvas = new MeasureCanvas()
): string {
	// subtract width of the truncation string from the provided max width so we have the max alowable space for the text
	const trueMaxLength = maxWidth - measureCanvas.getTextWidth(truncationString, fontTemplate);
	const truncatedText =
		truncateRecursivelyOnPixelWidth(measureCanvas, trueMaxLength, text, fontTemplate) + truncationString;

	return truncatedText;
}

/**
 * recurrsively truncates text to fit within a given width
 */
const truncateRecursivelyOnPixelWidth = (
	measureCanvas: MeasureCanvas,
	maxChunkWidth: number,
	text: string = "",
	fontTemplate: string
): string => {
	const firstHalf = text?.slice(0, Math.floor(text?.length / 2));
	const firstHalfLength = measureCanvas.getTextWidth(firstHalf, fontTemplate);

	if (firstHalf.length <= 1) {
		return "";
	}

	if (firstHalfLength > maxChunkWidth) {
		return truncateRecursivelyOnPixelWidth(measureCanvas, maxChunkWidth, firstHalf, fontTemplate);
	}

	const secondHalf = text?.slice(firstHalf.length, text?.length);
	const secondHalfLength = measureCanvas.getTextWidth(secondHalf, fontTemplate);
	const remainingLength = Math.floor(maxChunkWidth - firstHalfLength);

	if (secondHalfLength >= remainingLength) {
		return firstHalf + truncateRecursivelyOnPixelWidth(measureCanvas, remainingLength, secondHalf, fontTemplate);
	}

	return "";
};

/**
 * builds fontTemplate string for measuring text width in px
 */
export const buildFontTemplate = (size: string, weight: string, fontFamily = "Source Sans Pro"): string => {
	const fontSize = getFontSize(size);
	const fontWeight = getFontWeight(weight);

	return `${fontWeight} ${fontSize}px ${fontFamily}`;
};

// These sizes are fron the typography in our design system
// https://www.figma.com/file/eujlKvoVEVyxe4l1JMsivU/PY-Design-System-v1.0?type=design&node-id=4-657&mode=design&t=bgpOwTMQ0cmNSx7b-0
export const TypographySizeMap = {
	"exta-small": { height: 12, width: 10 },
	caption: { height: 16, width: 12 },
	"paragraph-3": { height: 16, width: 14 },
	"paragraph-2": { height: 24, width: 16 },
	"paragraph-1": { height: 24, width: 18 },
	"sub-heading": { height: 32, width: 20 },
	"heading-5": { height: 32, width: 24 },
	"heading-4": { height: 40, width: 28 },
	"heading-3": { height: 40, width: 32 },
	"heading-2": { height: 48, width: 40 },
	"heading-1": { height: 56, width: 48 },
};
